import React, { useState, useEffect, useCallback } from 'react'
import { X, Building2, Users, ExternalLink, Edit2, Plus, Trash2, Loader2, Star, Tag, Layers, DollarSign, FileText } from 'lucide-react'
import { Card, Button, StatusBadge } from '../ui'
import Tooltip from '../ui/Tooltip'
import { Partner } from '../../types/partner'
import { Contact } from '../../types/contact'
import { contactService } from '../../services/contactService'
import { getPartnerLogoUrl } from '../../services/partnerService'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { useToast } from '../../contexts/ToastContext'
import { formatCurrency, formatNumber } from '../../utils/formatters'
import AddContactDrawer from '../contacts/AddContactDrawer'
import EditContactDrawer from '../contacts/EditContactDrawer'
import DeleteContactModal from '../contacts/DeleteContactModal'

interface PartnerDetailDrawerProps {
  isOpen: boolean
  onClose: () => void
  partner: Partner | null
}

type DetailTab = 'details' | 'contacts'

const PartnerDetailDrawer: React.FC<PartnerDetailDrawerProps> = ({
  isOpen,
  onClose,
  partner
}) => {
  const { selectedCustomer } = useGlobalCustomerStore()
  const { showError } = useToast()

  const [activeTab, setActiveTab] = useState<DetailTab>('details')
  const [contacts, setContacts] = useState<Contact[]>([])
  const [contactsLoading, setContactsLoading] = useState(false)
  const [contactCount, setContactCount] = useState(0)

  // Drawer states
  const [isAddContactDrawerOpen, setIsAddContactDrawerOpen] = useState(false)
  const [isEditContactDrawerOpen, setIsEditContactDrawerOpen] = useState(false)
  const [isDeleteContactModalOpen, setIsDeleteContactModalOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)

  // Load contacts for a specific partner - accepts partnerId to avoid stale closures
  const loadContactsForPartner = useCallback(async (partnerId: string) => {
    if (!selectedCustomer?.id) return

    setContactsLoading(true)
    try {
      const { data } = await contactService.getContacts(
        String(selectedCustomer.id),
        undefined, // search
        partnerId
      )
      setContacts(data)
      // Use data.length since summary.total_contacts is unfiltered
      setContactCount(data.length)
    } catch {
      showError('Error', 'Failed to load contacts')
    } finally {
      setContactsLoading(false)
    }
  }, [selectedCustomer?.id, showError])

  // Reset and load when partner changes or drawer opens
  useEffect(() => {
    // Always reset first
    setContacts([])
    setContactCount(0)
    setActiveTab('details')

    // Then load if drawer is open with a valid partner
    if (isOpen && partner?.id) {
      loadContactsForPartner(partner.id)
    }
  }, [isOpen, partner?.id, loadContactsForPartner])

  // Reload contacts when switching to contacts tab
  useEffect(() => {
    if (isOpen && partner?.id && activeTab === 'contacts') {
      loadContactsForPartner(partner.id)
    }
  }, [activeTab, isOpen, partner?.id, loadContactsForPartner])

  // Helper to reload contacts (used by child components)
  const loadPartnerContacts = useCallback(() => {
    if (partner?.id) {
      loadContactsForPartner(partner.id)
    }
  }, [partner?.id, loadContactsForPartner])

  const handleAddContact = () => {
    setIsAddContactDrawerOpen(true)
  }

  const handleContactCreated = () => {
    setIsAddContactDrawerOpen(false)
    loadPartnerContacts()
  }

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact)
    setIsEditContactDrawerOpen(true)
  }

  const handleContactUpdated = () => {
    setIsEditContactDrawerOpen(false)
    setSelectedContact(null)
    loadPartnerContacts()
  }

  const handleDeleteContact = (contact: Contact) => {
    setSelectedContact(contact)
    setIsDeleteContactModalOpen(true)
  }

  const handleContactDeleted = () => {
    setIsDeleteContactModalOpen(false)
    setSelectedContact(null)
    loadPartnerContacts()
  }

  if (!isOpen) return null

  return (
    <>
      <div className='fixed inset-0 z-50 overflow-hidden'>
        <div
          className='absolute inset-0 bg-black bg-opacity-50'
          onClick={onClose}
        />

        <div className='absolute right-0 top-0 h-full w-full max-w-3xl bg-white shadow-xl'>
          <div className='flex h-full flex-col'>
            {/* Header */}
            <div className='flex items-center justify-between px-6 py-4 border-b border-gray-200'>
              <div className='flex items-center space-x-3'>
                {partner?.website_url ? (
                  <a
                    href={partner.website_url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex items-center space-x-3 group'
                  >
                    {partner?.logo_url && selectedCustomer?.id ? (
                      <div className='h-14 w-14 bg-white border border-gray-200 rounded-lg flex items-center justify-center ring-2 ring-transparent group-hover:ring-blue-200 transition-all'>
                        <img
                          src={getPartnerLogoUrl(partner.id, String(selectedCustomer.id), partner.updated_at)}
                          alt={partner.name}
                          className='h-12 w-12 object-contain'
                          crossOrigin='anonymous'
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      </div>
                    ) : (
                      <div className='p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors'>
                        <Building2 className='h-6 w-6 text-blue-600' />
                      </div>
                    )}
                    <div className='flex items-center space-x-1.5'>
                      <h2 className='text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors'>
                        {partner?.name}
                      </h2>
                      <ExternalLink className='h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors' />
                    </div>
                  </a>
                ) : (
                  <div className='flex items-center space-x-3'>
                    {partner?.logo_url && selectedCustomer?.id ? (
                      <div className='h-14 w-14 bg-white border border-gray-200 rounded-lg flex items-center justify-center'>
                        <img
                          src={getPartnerLogoUrl(partner.id, String(selectedCustomer.id), partner.updated_at)}
                          alt={partner.name}
                          className='h-12 w-12 object-contain'
                          crossOrigin='anonymous'
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      </div>
                    ) : (
                      <div className='p-3 bg-blue-100 rounded-lg'>
                        <Building2 className='h-6 w-6 text-blue-600' />
                      </div>
                    )}
                    <h2 className='text-xl font-semibold text-gray-900'>
                      {partner?.name}
                    </h2>
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className='p-2 hover:bg-gray-100 rounded-lg transition-colors'
              >
                <X className='h-5 w-5 text-gray-400' />
              </button>
            </div>

            {/* Tabs */}
            <div className='border-b border-gray-200'>
              <nav className='flex space-x-8 px-6'>
                <button
                  onClick={() => setActiveTab('details')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'details'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className='flex items-center space-x-2'>
                    <Building2 className='h-4 w-4' />
                    <span>Details</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('contacts')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'contacts'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className='flex items-center space-x-2'>
                    <Users className='h-4 w-4' />
                    <span>Contacts ({contactCount})</span>
                  </div>
                </button>
              </nav>
            </div>

            {/* Content */}
            <div className='flex-1 overflow-y-auto'>
              {activeTab === 'details' && partner && (
                <div className='p-5 space-y-4'>
                  {/* Quick Info Pills */}
                  <div className='flex flex-wrap items-center gap-2 py-3 border-b border-gray-100'>
                    <div className='inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full'>
                      <Tag className='h-3 w-3 text-gray-500' />
                      <span className='text-xs font-medium text-gray-700'>{partner.partner_categories.name}</span>
                    </div>
                    {partner.partner_tiers && (
                      <div className='inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-full'>
                        <Layers className='h-3 w-3 text-blue-500' />
                        <span className='text-xs font-medium text-blue-700'>{partner.partner_tiers.name}</span>
                      </div>
                    )}
                    <StatusBadge status={partner.status} />
                  </div>

                  {/* Description */}
                  {partner.description && (
                    <div className='bg-gray-50 rounded-lg p-3 border border-gray-100'>
                      <p className='text-xs text-gray-600 leading-relaxed'>
                        {partner.description}
                      </p>
                    </div>
                  )}

                  {/* Performance Metrics */}
                  <div className='grid grid-cols-2 gap-3'>
                    <div className='relative overflow-hidden bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg p-3 border border-emerald-100'>
                      <div className='absolute top-2 right-2 h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center'>
                        <DollarSign className='h-4 w-4 text-emerald-600' />
                      </div>
                      <p className='text-xs font-medium text-emerald-600 uppercase tracking-wide'>
                        Total EMV
                      </p>
                      <p className='text-lg font-bold text-gray-900 mt-1'>
                        {formatCurrency(partner.metrics.total_emv)}
                      </p>
                    </div>
                    <div className='relative overflow-hidden bg-gradient-to-br from-violet-50 to-purple-50 rounded-lg p-3 border border-violet-100'>
                      <div className='absolute top-2 right-2 h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center'>
                        <FileText className='h-4 w-4 text-violet-600' />
                      </div>
                      <p className='text-xs font-medium text-violet-600 uppercase tracking-wide'>
                        Total Posts
                      </p>
                      <p className='text-lg font-bold text-gray-900 mt-1'>
                        {formatNumber(partner.metrics.total_posts)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'contacts' && partner && (
                <div className='p-6'>
                  {/* Add Contact Button */}
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-sm font-medium text-gray-900'>
                      Sponsor Contacts
                    </h3>
                    <Button
                      variant='primary'
                      size='sm'
                      onClick={handleAddContact}
                      className='text-xs'
                    >
                      <Plus className='h-3.5 w-3.5 mr-1.5' />
                      Add Contact
                    </Button>
                  </div>

                  {/* Contacts Table */}
                  {contactsLoading ? (
                    <div className='text-center py-12'>
                      <Loader2 className='h-8 w-8 animate-spin text-blue-600 mx-auto' />
                      <p className='text-gray-500 mt-2'>Loading contacts...</p>
                    </div>
                  ) : contacts.length === 0 ? (
                    <Card variant='outlined' className='p-8 text-center'>
                      <Users className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                      <p className='text-gray-500 mb-4'>
                        No contacts found for this sponsor.
                      </p>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={handleAddContact}
                        className='text-xs'
                      >
                        <Plus className='h-3.5 w-3.5 mr-1.5' />
                        Add First Contact
                      </Button>
                    </Card>
                  ) : (
                    <Card variant='outlined'>
                      <div className='overflow-x-auto'>
                        <table className='w-full'>
                          <thead>
                            <tr className='border-b border-gray-200 bg-gray-50'>
                              <th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                                Name
                              </th>
                              <th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                                Role
                              </th>
                              <th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                                Email
                              </th>
                              <th className='px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                                Status
                              </th>
                              <th className='px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className='divide-y divide-gray-200'>
                            {contacts.map((contact) => (
                              <tr key={contact.id} className='hover:bg-gray-50'>
                                <td className='px-3 py-2'>
                                  <div>
                                    <p className='text-xs font-medium text-gray-900'>
                                      {contact.last_name}, {contact.first_name}
                                      {contact.is_primary && (
                                        <Tooltip content='Primary contact'>
                                          <Star className='h-3 w-3 text-yellow-500 inline ml-1 fill-yellow-500' />
                                        </Tooltip>
                                      )}
                                    </p>
                                    {contact.job_title && (
                                      <p className='text-xs text-gray-500'>
                                        {contact.job_title}
                                      </p>
                                    )}
                                  </div>
                                </td>
                                <td className='px-3 py-2'>
                                  <span className='text-xs text-gray-900'>
                                    {contact.contact_roles.name}
                                  </span>
                                </td>
                                <td className='px-3 py-2'>
                                  {contact.email ? (
                                    <a
                                      href={`mailto:${contact.email}`}
                                      className='text-xs text-blue-600 hover:underline'
                                    >
                                      {contact.email}
                                    </a>
                                  ) : (
                                    <span className='text-xs text-gray-400'>-</span>
                                  )}
                                </td>
                                <td className='px-3 py-2 text-center'>
                                  <StatusBadge status={contact.status} />
                                </td>
                                <td className='px-3 py-2'>
                                  <div className='flex items-center justify-end gap-1'>
                                    <Tooltip content='Edit contact'>
                                      <button
                                        onClick={() => handleEditContact(contact)}
                                        className='p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors'
                                      >
                                        <Edit2 className='h-3.5 w-3.5' />
                                      </button>
                                    </Tooltip>
                                    <Tooltip content='Delete contact'>
                                      <button
                                        onClick={() => handleDeleteContact(contact)}
                                        className='p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors'
                                      >
                                        <Trash2 className='h-3.5 w-3.5' />
                                      </button>
                                    </Tooltip>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Contact Drawer */}
      <AddContactDrawer
        isOpen={isAddContactDrawerOpen}
        onClose={() => setIsAddContactDrawerOpen(false)}
        preselectedPartner={partner}
        onContactCreated={handleContactCreated}
      />

      {/* Edit Contact Drawer */}
      <EditContactDrawer
        isOpen={isEditContactDrawerOpen}
        onClose={() => {
          setIsEditContactDrawerOpen(false)
          setSelectedContact(null)
        }}
        contact={selectedContact}
        onContactUpdated={handleContactUpdated}
      />

      {/* Delete Contact Modal */}
      <DeleteContactModal
        isOpen={isDeleteContactModalOpen}
        onClose={() => {
          setIsDeleteContactModalOpen(false)
          setSelectedContact(null)
        }}
        contact={selectedContact}
        onContactDeleted={handleContactDeleted}
      />
    </>
  )
}

export default PartnerDetailDrawer
